// ── Player-to-player interaction UI (DOM overlay). ──
// One premium, emerald/gold component that owns every social surface launched from a nearby
// player: the radial-ish proximity MENU (Message / Heart / Challenge), the whisper text input,
// the gold-wager prompt, the "waiting for…" pill, the incoming-challenge modal, heart toasts,
// and the duel-result banner. It is purely presentational — it calls back into NetClient and
// never touches game state. Degrades to nothing when offline (the world only opens it online).
//
// Anchoring: the proximity menu is positioned at a PAGE coordinate (the world scene converts a
// remote sprite's world position → screen → page each frame and calls `trackAnchor`), so the
// menu follows the target as it (or the camera) moves. Everything tears down on `destroy()`.

export interface InteractionMenuCallbacks {
  /** Send a private message to the open target. */
  onWhisper: (targetSessionId: string, text: string) => void;
  /** Send a ❤️ to the open target. */
  onHeart: (targetSessionId: string) => void;
  /** Challenge the open target to a 1v1 for `wager` gold. */
  onChallenge: (targetSessionId: string, wager: number) => void;
  /** Respond to an incoming challenge. */
  onChallengeResponse: (fromSessionId: string, accept: boolean) => void;
  /** Current spendable gold — clamps the wager input. */
  getGold: () => number;
}

const EMERALD = '#34d399';
const GOLD = '#fbbf24';
const PANEL_BG = 'rgba(10,14,20,0.86)';
const PANEL_BORDER = `1px solid ${EMERALD}66`;

function isTouchDevice(): boolean {
  return (
    (typeof window !== 'undefined' &&
      ((window.matchMedia && window.matchMedia('(pointer: coarse)').matches) || 'ontouchstart' in window)) ||
    false
  );
}

/** Open target of the proximity menu / sub-prompts. */
interface MenuTarget {
  sessionId: string;
  name: string;
}

export class InteractionMenu {
  #cb: InteractionMenuCallbacks;
  #isTouch = isTouchDevice();

  /** Anchored proximity menu + its sub-views (whisper / wager / waiting), all inside one panel. */
  #menu: HTMLDivElement;
  #menuBody: HTMLDivElement;
  #menuTitle: HTMLDivElement;
  #target: MenuTarget | null = null;

  /** Fixed modal for an INCOMING challenge (Accept / Decline). */
  #modal: HTMLDivElement;
  #modalText: HTMLDivElement;
  #incomingFrom: string | null = null;

  /** Tap-away closer (registered only while the menu is open). */
  #outsidePointer?: (e: Event) => void;
  #escHandler?: (e: KeyboardEvent) => void;

  constructor(cb: InteractionMenuCallbacks) {
    this.#cb = cb;

    // ── Anchored proximity menu ──
    this.#menu = document.createElement('div');
    this.#menu.style.cssText = [
      'position:fixed',
      'transform:translate(-50%,-100%)', // anchor at the bottom-center → sits above the target
      'min-width:172px',
      'padding:10px',
      'border-radius:14px',
      `border:${PANEL_BORDER}`,
      `background:${PANEL_BG}`,
      'backdrop-filter:blur(10px)',
      `box-shadow:0 10px 30px rgba(0,0,0,0.5), 0 0 0 1px rgba(52,211,153,0.12)`,
      'font-family:system-ui,sans-serif',
      'color:#eaeaea',
      'z-index:1200',
      'display:none',
      'pointer-events:auto',
    ].join(';');

    this.#menuTitle = document.createElement('div');
    this.#menuTitle.style.cssText = `font-weight:700;font-size:13px;margin-bottom:8px;color:${EMERALD};text-align:center;letter-spacing:0.2px`;
    this.#menu.appendChild(this.#menuTitle);

    this.#menuBody = document.createElement('div');
    this.#menu.appendChild(this.#menuBody);
    document.body.appendChild(this.#menu);

    // ── Incoming-challenge modal ──
    this.#modal = document.createElement('div');
    this.#modal.style.cssText = [
      'position:fixed',
      'left:50%',
      'top:34%',
      'transform:translate(-50%,-50%)',
      'width:min(86vw,340px)',
      'padding:20px',
      'border-radius:18px',
      `border:1px solid ${GOLD}88`,
      `background:${PANEL_BG}`,
      'backdrop-filter:blur(12px)',
      'box-shadow:0 18px 50px rgba(0,0,0,0.6)',
      'font-family:system-ui,sans-serif',
      'color:#fff',
      'text-align:center',
      'z-index:1300',
      'display:none',
    ].join(';');
    const modalIcon = document.createElement('div');
    modalIcon.textContent = '⚔️';
    modalIcon.style.cssText = 'font-size:34px;margin-bottom:8px';
    this.#modalText = document.createElement('div');
    this.#modalText.style.cssText = 'font-size:15px;line-height:1.4;margin-bottom:16px';
    const modalRow = document.createElement('div');
    modalRow.style.cssText = 'display:flex;gap:10px';
    const declineBtn = this.#actionButton('Decline', '#ef4444');
    const acceptBtn = this.#actionButton('Accept', EMERALD);
    declineBtn.style.flex = '1';
    acceptBtn.style.flex = '1';
    declineBtn.onclick = () => this.#respondIncoming(false);
    acceptBtn.onclick = () => this.#respondIncoming(true);
    modalRow.appendChild(declineBtn);
    modalRow.appendChild(acceptBtn);
    this.#modal.appendChild(modalIcon);
    this.#modal.appendChild(this.#modalText);
    this.#modal.appendChild(modalRow);
    document.body.appendChild(this.#modal);
  }

  // ── Proximity menu lifecycle ────────────────────────────────────────────────

  /** Whether the anchored proximity menu (or one of its sub-views) is open. */
  get isOpen(): boolean {
    return this.#menu.style.display !== 'none';
  }

  /** Whether a DOM text input inside the menu currently has focus (so the game ignores keys). */
  get isTyping(): boolean {
    const a = document.activeElement;
    return !!a && this.#menu.contains(a) && (a.tagName === 'INPUT' || a.tagName === 'TEXTAREA');
  }

  /** Open the 3-option proximity menu for a target at a page coordinate. */
  open(target: MenuTarget, pageX: number, pageY: number): void {
    this.#target = target;
    this.#menuTitle.textContent = target.name;
    this.#showRootMenu();
    this.#menu.style.left = `${pageX}px`;
    this.#menu.style.top = `${pageY}px`;
    this.#menu.style.display = '';
    this.#bindDismissers();
  }

  /** Re-position the open menu (the world scene calls this each frame to follow the sprite). */
  trackAnchor(pageX: number, pageY: number): void {
    if (!this.isOpen) return;
    this.#menu.style.left = `${pageX}px`;
    this.#menu.style.top = `${pageY}px`;
  }

  /** Close the anchored menu + sub-views (does NOT touch the incoming-challenge modal). */
  close(): void {
    this.#menu.style.display = 'none';
    this.#target = null;
    this.#unbindDismissers();
  }

  #showRootMenu(): void {
    this.#menuBody.replaceChildren();
    const grid = document.createElement('div');
    grid.style.cssText = 'display:flex;flex-direction:column;gap:6px';
    grid.appendChild(this.#menuItem('💬', 'Message', () => this.#showWhisper()));
    grid.appendChild(
      this.#menuItem('❤️', 'Heart', () => {
        if (this.#target) this.#cb.onHeart(this.#target.sessionId);
        this.close();
      }),
    );
    grid.appendChild(this.#menuItem('⚔️', 'Challenge (1v1)', () => this.#showWager()));
    this.#menuBody.replaceChildren(grid);
  }

  #showWhisper(): void {
    const wrap = document.createElement('div');
    wrap.style.cssText = 'display:flex;flex-direction:column;gap:8px';
    const input = document.createElement('input');
    input.type = 'text';
    input.placeholder = 'Message…';
    input.maxLength = 200;
    input.style.cssText = this.#inputCss();
    input.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') this.#submitWhisper(input.value);
      else if (e.key === 'Escape') this.close();
    });
    const send = this.#actionButton('Send', EMERALD);
    send.onclick = () => this.#submitWhisper(input.value);
    wrap.appendChild(input);
    wrap.appendChild(this.#row(this.#backButton(() => this.#showRootMenu()), send));
    this.#menuBody.replaceChildren(wrap);
    requestAnimationFrame(() => input.focus());
  }

  #submitWhisper(raw: string): void {
    const text = raw.trim();
    if (this.#target && text) this.#cb.onWhisper(this.#target.sessionId, text);
    this.close();
  }

  #showWager(): void {
    const maxGold = Math.max(0, Math.floor(this.#cb.getGold()));
    const wrap = document.createElement('div');
    wrap.style.cssText = 'display:flex;flex-direction:column;gap:8px';

    const label = document.createElement('div');
    label.style.cssText = `font-size:12px;color:${GOLD}`;
    label.textContent = `Wager (you have ${maxGold} Gold)`;

    const input = document.createElement('input');
    input.type = 'number';
    input.min = '0';
    input.max = String(maxGold);
    input.value = String(Math.min(50, maxGold));
    input.style.cssText = this.#inputCss();
    input.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') this.#submitWager(input.value, maxGold);
      else if (e.key === 'Escape') this.close();
    });

    const send = this.#actionButton('Challenge', GOLD, '#1a1206');
    send.onclick = () => this.#submitWager(input.value, maxGold);
    wrap.appendChild(label);
    wrap.appendChild(input);
    wrap.appendChild(this.#row(this.#backButton(() => this.#showRootMenu()), send));
    this.#menuBody.replaceChildren(wrap);
    requestAnimationFrame(() => input.select());
  }

  #submitWager(raw: string, maxGold: number): void {
    if (!this.#target) {
      this.close();
      return;
    }
    const wager = Math.max(0, Math.min(maxGold, Math.floor(Number(raw) || 0)));
    this.#cb.onChallenge(this.#target.sessionId, wager);
    this.#showWaiting(this.#target.name);
  }

  /** Swap the menu body to a "Waiting for <name>…" pill (challenge sent, awaiting their reply). */
  #showWaiting(name: string): void {
    const pill = document.createElement('div');
    pill.style.cssText = `display:flex;align-items:center;gap:8px;justify-content:center;font-size:13px;color:${EMERALD};padding:4px 0`;
    pill.innerHTML = `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${EMERALD};animation:mn-pulse 1s ease-in-out infinite"></span> Waiting for ${escapeHtml(name)}…`;
    this.#ensurePulseKeyframes();
    const cancel = this.#backButton(() => this.close(), 'Cancel');
    const wrap = document.createElement('div');
    wrap.style.cssText = 'display:flex;flex-direction:column;gap:10px';
    wrap.appendChild(pill);
    wrap.appendChild(cancel);
    this.#menuBody.replaceChildren(wrap);
  }

  // ── Incoming-challenge modal ────────────────────────────────────────────────

  /** Show the Accept/Decline modal for an incoming challenge. */
  showIncomingChallenge(fromSessionId: string, fromUsername: string, wager: number): void {
    this.#incomingFrom = fromSessionId;
    this.#modalText.innerHTML = `<b style="color:${GOLD}">${escapeHtml(fromUsername)}</b> challenges you to a 1v1 for <b style="color:${GOLD}">${wager} Gold</b>!`;
    this.#modal.style.display = '';
  }

  /** Dismiss the incoming-challenge modal without responding (e.g. on `challengeExpired`). */
  dismissIncomingChallenge(): void {
    this.#modal.style.display = 'none';
    this.#incomingFrom = null;
  }

  #respondIncoming(accept: boolean): void {
    const from = this.#incomingFrom;
    this.#modal.style.display = 'none';
    this.#incomingFrom = null;
    if (from) this.#cb.onChallengeResponse(from, accept);
  }

  // ── Transient feedback: floating heart / toasts / result banner ─────────────

  /** A small toast in the top-center (heart received, declined, cancelled, etc.). */
  toast(text: string, color = '#eaeaea'): void {
    const el = document.createElement('div');
    el.textContent = text;
    el.style.cssText = [
      'position:fixed',
      'left:50%',
      'top:14%',
      'transform:translateX(-50%)',
      'padding:10px 16px',
      'border-radius:12px',
      `border:1px solid ${color}66`,
      `background:${PANEL_BG}`,
      'backdrop-filter:blur(8px)',
      `color:${color}`,
      'font-family:system-ui,sans-serif',
      'font-size:14px',
      'font-weight:600',
      'box-shadow:0 8px 24px rgba(0,0,0,0.5)',
      'z-index:1400',
      'pointer-events:none',
      'opacity:0',
      'transition:opacity 0.25s ease, transform 0.25s ease',
    ].join(';');
    document.body.appendChild(el);
    requestAnimationFrame(() => {
      el.style.opacity = '1';
      el.style.transform = 'translateX(-50%) translateY(4px)';
    });
    window.setTimeout(() => {
      el.style.opacity = '0';
      window.setTimeout(() => el.remove(), 300);
    }, 2200);
  }

  /** A bold center banner for the duel result (won / lost). */
  banner(text: string, color = GOLD): void {
    const el = document.createElement('div');
    el.textContent = text;
    el.style.cssText = [
      'position:fixed',
      'left:50%',
      'top:30%',
      'transform:translate(-50%,-50%) scale(0.9)',
      'padding:18px 28px',
      'border-radius:18px',
      `border:1px solid ${color}`,
      `background:${PANEL_BG}`,
      'backdrop-filter:blur(12px)',
      `color:${color}`,
      'font-family:Kenney-Future-Narrow, system-ui, sans-serif',
      'font-size:24px',
      'font-weight:800',
      'text-align:center',
      'text-shadow:0 2px 8px rgba(0,0,0,0.6)',
      'box-shadow:0 18px 50px rgba(0,0,0,0.6)',
      'z-index:1400',
      'pointer-events:none',
      'opacity:0',
      'transition:opacity 0.3s ease, transform 0.3s ease',
    ].join(';');
    document.body.appendChild(el);
    requestAnimationFrame(() => {
      el.style.opacity = '1';
      el.style.transform = 'translate(-50%,-50%) scale(1)';
    });
    window.setTimeout(() => {
      el.style.opacity = '0';
      window.setTimeout(() => el.remove(), 350);
    }, 2600);
  }

  // ── small DOM builders ──────────────────────────────────────────────────────

  #menuItem(icon: string, text: string, onClick: () => void): HTMLButtonElement {
    const b = document.createElement('button');
    b.innerHTML = `<span style="font-size:16px">${icon}</span><span>${text}</span>`;
    b.style.cssText = [
      'display:flex',
      'align-items:center',
      'gap:10px',
      'width:100%',
      'box-sizing:border-box',
      'padding:10px 12px',
      'border-radius:10px',
      'border:1px solid rgba(255,255,255,0.08)',
      'background:rgba(255,255,255,0.04)',
      'color:#eaeaea',
      'font-size:14px',
      'font-family:inherit',
      'text-align:left',
      'cursor:pointer',
      'pointer-events:auto',
    ].join(';');
    b.onmouseenter = () => {
      b.style.background = `${EMERALD}22`;
      b.style.borderColor = `${EMERALD}66`;
    };
    b.onmouseleave = () => {
      b.style.background = 'rgba(255,255,255,0.04)';
      b.style.borderColor = 'rgba(255,255,255,0.08)';
    };
    b.onclick = onClick;
    return b;
  }

  #actionButton(label: string, bg: string, fg = '#06241a'): HTMLButtonElement {
    const b = document.createElement('button');
    b.textContent = label;
    b.style.cssText = [
      'padding:9px 14px',
      'border-radius:10px',
      'border:none',
      `background:${bg}`,
      `color:${fg}`,
      'font-size:13px',
      'font-weight:700',
      'font-family:inherit',
      'cursor:pointer',
      'pointer-events:auto',
    ].join(';');
    return b;
  }

  #backButton(onClick: () => void, label = 'Back'): HTMLButtonElement {
    const b = document.createElement('button');
    b.textContent = label;
    b.style.cssText = [
      'padding:9px 12px',
      'border-radius:10px',
      'border:1px solid rgba(255,255,255,0.12)',
      'background:transparent',
      'color:#b9b9c9',
      'font-size:13px',
      'font-family:inherit',
      'cursor:pointer',
      'pointer-events:auto',
    ].join(';');
    b.onclick = onClick;
    return b;
  }

  #row(a: HTMLElement, b: HTMLElement): HTMLDivElement {
    const r = document.createElement('div');
    r.style.cssText = 'display:flex;gap:8px';
    a.style.flex = '1';
    b.style.flex = '1';
    r.appendChild(a);
    r.appendChild(b);
    return r;
  }

  #inputCss(): string {
    return [
      'width:100%',
      'box-sizing:border-box',
      'padding:9px 10px',
      'border-radius:10px',
      'border:1px solid rgba(255,255,255,0.15)',
      'background:rgba(20,22,30,0.6)',
      'color:#fff',
      'outline:none',
      'font-size:13px',
      'font-family:inherit',
      'pointer-events:auto',
    ].join(';');
  }

  #ensurePulseKeyframes(): void {
    if (document.getElementById('mn-pulse-style')) return;
    const style = document.createElement('style');
    style.id = 'mn-pulse-style';
    style.textContent = '@keyframes mn-pulse{0%,100%{opacity:1}50%{opacity:0.25}}';
    document.head.appendChild(style);
  }

  // ── dismissers (tap-away + ESC), bound only while the menu is open ───────────

  #bindDismissers(): void {
    this.#unbindDismissers();
    this.#outsidePointer = (e: Event) => {
      if (!this.#menu.contains(e.target as Node)) this.close();
    };
    this.#escHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') this.close();
    };
    // capture-phase so the tap-away fires before world pointer handlers consume it
    window.addEventListener('pointerdown', this.#outsidePointer, true);
    window.addEventListener('keydown', this.#escHandler, true);
  }

  #unbindDismissers(): void {
    if (this.#outsidePointer) window.removeEventListener('pointerdown', this.#outsidePointer, true);
    if (this.#escHandler) window.removeEventListener('keydown', this.#escHandler, true);
    this.#outsidePointer = undefined;
    this.#escHandler = undefined;
  }

  destroy(): void {
    this.#unbindDismissers();
    this.#menu.remove();
    this.#modal.remove();
  }
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
